import { z } from 'zod';

/**
 * Card dates — the contract layer for block **B24**, stage 1.
 *
 * `BE-24.1` (`FR-CARD-013`, an optional start date and an optional due date,
 * each an absolute instant, field-scoped per SC-4) and `BE-24.2`
 * (`FR-CARD-014`, the due-date reminder offset — a property of the CARD, not
 * of the board; `README.md` §C.2 and `docs/requirements/09-*.md`'s
 * `FR-NOTIF-003` record that ruling as settled).
 *
 * ---------------------------------------------------------------------------
 * ALL INPUT, NO VIEW — `./board-filter`'S SHAPE, FOR A DIFFERENT REASON
 * ---------------------------------------------------------------------------
 * `./board-filter` declares no view because a filter narrows an existing
 * response rather than answering with one of its own. This file declares no
 * view for a *different* reason: the three fields it puts on the wire —
 * `startAt`, `dueAt`, `dueReminderMinutesBefore` — are read on
 * `cardDetailSchema` (`./card-detail`) and `boardFaceCardSchema`
 * (`./board-contents`), the two shapes that already own a card's view, on
 * exactly the rule `./card.ts`'s header states: a field arrives with the
 * route that reads or writes it, on the shape that route already answers
 * with. A `cardDatesSchema` declared here and nested into both would be a
 * second card view kept in step with the first by hand, which is the
 * `boardFaceCardSchema`/`cardSchema` divergence this package has avoided at
 * every badge block so far.
 *
 * ---------------------------------------------------------------------------
 * THREE ROUTES, THREE BODIES — NEVER ONE COMPOUND `PATCH .../dates`
 * ---------------------------------------------------------------------------
 * `FR-CARD-013` AC4 describes a single "Remove" gesture clearing both dates
 * at once, and the temptation is a body that accepts `startAt` and `dueAt`
 * together. SC-4 (field-scoped patches) and `FR-EXP-031`'s per-field
 * last-write-wins both refuse it, on `cardTitlePatchSchema`'s argument
 * restated for the third field-scoped noun on this table: an absent key and a
 * cleared value cannot be told apart in a body that carries both fields at
 * once, so a client that means "leave the due date alone" and one that means
 * "clear it" would send the same request. AC4's one gesture is therefore two
 * requests from the client — `PATCH .../start-date { startAt: null }` and
 * `PATCH .../due-date { dueAt: null }` — and two activity rows
 * (`start.date_removed`, `due.date_removed`), which is what
 * `FR-COLLAB-017`'s enumeration lists separately and is correct rather than a
 * cost.
 *
 * The reminder offset is a fourth field and a **third** route rather than a
 * key on the due-date body, for the reason `cardDueReminderPatchSchema`
 * states at its own declaration: the due-date route already has three
 * outcomes for the offset (default it, preserve it, clear it) that depend on
 * the *transition*, not on a value the caller sends, so a caller-supplied
 * offset on that body would have to be reconciled against a value the route
 * is simultaneously computing for itself.
 *
 * ---------------------------------------------------------------------------
 * INSTANTS ON THE WAY IN ARE STRICT ISO-8601; ON THE WAY OUT THEY ARE
 * POSTGRES' OWN SPELLING — SEE `cardDetailSchema.startAt`
 * ---------------------------------------------------------------------------
 * This is the *opposite* asymmetry from every other timestamp in this
 * package, and it is worth stating plainly because every existing precedent
 * (`createdAt`, `editedAt`, `joinedAt`, …) is read-only and therefore only
 * ever needs the lenient, Postgres-shaped side. A start or due date is the
 * first timestamp in this product a **client sends**, so the strict side
 * exists for the first time too: `cardDateInstantSchema` below is
 * `z.iso.datetime({ offset: true })`, which refuses Postgres' own
 * space-separated, no-`Z` spelling — correctly, because a client should never
 * be echoing that spelling back as a request. Pinning the *view* the same way
 * would 500 on every read, which `card.ts`'s and `comment.ts`'s docstrings
 * both name as the rejected alternative for exactly that reason.
 */

// ---------------------------------------------------------------------------
// INPUT primitives — what a route ACCEPTS.
// ---------------------------------------------------------------------------

/**
 * One absolute instant, as a start or due date arrives from a client —
 * `FR-CARD-013`: "a date and a time of day". `{ offset: true }` accepts both
 * a trailing `Z` and an explicit `+HH:MM`/`-HH:MM` offset, so a client in any
 * time zone can send the instant it means without first converting to UTC
 * itself; `FR-CARD-013`'s own edge case — "dates are interpreted in the
 * viewing user's time zone" — is exactly this: the wire carries one absolute
 * instant and each viewer's client renders it in their own zone
 * (`BE-44.1`'s, not this block's).
 *
 * Not exported: every caller of it is a field on a `strictObject` a few lines
 * below, and a bare instant schema with no field name attached is not a
 * shape a route ever accepts on its own.
 */
export const cardDateInstantSchema = z.iso.datetime({ offset: true });

/**
 * `PATCH /cards/:id/start-date` — `FR-CARD-013`. SC-4, field-scoped.
 *
 * `null` clears the date — `FR-CARD-013` AC4's Remove gesture, this field's
 * half of it. There is no default-to-now and no "today" shorthand: the
 * requirement's calendar picker always names an explicit instant, and a
 * server-side default would be a value the client never actually chose.
 *
 * ⚠ **There is no `start.date_changed`, and this body's value→value case
 * (an existing start date replaced by a different one) emits
 * `start.date_set` again** — see `packages/shared/src/events.ts`'s B24
 * entries for the full argument. In short: nothing in `FR-CARD-013`
 * distinguishes *setting* a start date from *changing* one the way
 * `FR-CARD-014` AC4 distinguishes a due-date change from a due-date set (the
 * reminder-offset recompute), so one verb covers both — the
 * `card.renamed`/`board.renamed`/`list.renamed`/`label.renamed` pattern, one
 * name for every value the field ever takes.
 */
export const cardStartDatePatchSchema = z.strictObject({
  startAt: cardDateInstantSchema
    .nullable()
    .describe(
      "The card's start date and time, as an absolute instant — an ISO-8601 " +
        'string with an explicit offset or a trailing "Z" (FR-CARD-013). Send ' +
        'null to clear it (FR-CARD-013 AC4).',
    ),
});

export type CardStartDatePatch = z.infer<typeof cardStartDatePatchSchema>;

/**
 * `PATCH /cards/:id/due-date` — `FR-CARD-013`, plus half of `FR-CARD-014`.
 * SC-4, field-scoped.
 *
 * `null` clears the date — `FR-CARD-013` AC4's Remove gesture, this field's
 * half of it, and it also clears the reminder offset: `due_at IS NULL AND
 * due_reminder_minutes_before IS NOT NULL` is an orphan state no CHECK, no FK
 * and no trigger forbids, and the route is the only thing that keeps a card
 * out of it. See `apps/server/src/cards/dates.ts`'s `resolveDueDateTransition`
 * for the three outcomes this one field's write can have for the reminder
 * offset — default it, preserve it, or clear it — none of which this body
 * lets the caller choose directly. Setting the offset itself is
 * `cardDueReminderPatchSchema`'s own route.
 */
export const cardDueDatePatchSchema = z.strictObject({
  dueAt: cardDateInstantSchema
    .nullable()
    .describe(
      "The card's due date and time, as an absolute instant — an ISO-8601 " +
        'string with an explicit offset or a trailing "Z" (FR-CARD-013). Send ' +
        'null to clear it (FR-CARD-013 AC4); clearing also removes the due-date ' +
        'reminder offset, since a reminder cannot outlive the date it is ' +
        'relative to.',
    ),
  /**
   * `FR-EXP-029` edge case (BE-44.7): `true` marks this due date as a
   * date-only (all-day) value — midnight-UTC-normalised `dueAt` rendered as
   * the same calendar date for every viewer rather than as an instant in the
   * viewer's timezone. Optional for back-compat: absent means `false`
   * (instant semantics, unchanged). `true` with `dueAt: null` is refused
   * (422) — an all-day flag with no date is the same orphan class as a
   * reminder offset with no date.
   */
  dueAllDay: z
    .boolean()
    .optional()
    .describe(
      'Whether the due date is a date-only (all-day) value rendered as the ' +
        'same calendar date for everyone (FR-EXP-029). Absent means false. ' +
        'Requires a midnight-UTC-normalised dueAt; true with null is refused.',
    ),
});

export type CardDueDatePatch = z.infer<typeof cardDueDatePatchSchema>;

/**
 * The reminder dropdown's fixed option set — `FR-CARD-014`'s own list, "at
 * the time of the due date, 5, 10 or 15 minutes before, 1 or 2 hours before,
 * and 1 or 2 days before", turned into minutes. `None` is the eighth value's
 * absence: it is spelt `null`, never a ninth number, on `bioPatchSchema`'s
 * rule that a state gets **one** representation.
 *
 * ⚠ **This is `ck_cards_due_reminder`'s own member list, restated rather than
 * imported** — there is no way to import a CHECK constraint's `ANY (ARRAY[…])`
 * into TypeScript, so this array and that constraint are two spellings of one
 * fact and must be kept in step by a reader, not by the compiler. Verified
 * against the live catalogue for this pass: `due_reminder_minutes_before IS
 * NULL OR = ANY (ARRAY[0, 5, 10, 15, 60, 120, 1440, 2880])`.
 */
export const DUE_REMINDER_OFFSETS = [0, 5, 10, 15, 60, 120, 1440, 2880] as const;

/**
 * `FR-CARD-014` AC1: "given a user sets a due date without touching the
 * reminder dropdown, when they save, then the reminder shows the
 * 24-hours-before default." 24 hours = 1,440 minutes, and it is
 * `FR-CARD-014`'s own number, not a round figure chosen here.
 */
export const DUE_REMINDER_DEFAULT_MINUTES_BEFORE = 1440;

/**
 * One legal reminder offset, or `null` for **None** — the view-side parser as
 * well as the input primitive, on `labelColourSchema`'s house rule for a
 * value read out of a column with a CHECK behind it: a value that fails to
 * parse means `DUE_REMINDER_OFFSETS` and `ck_cards_due_reminder` have
 * drifted, which a read should surface rather than pass through.
 *
 * `z.literal(DUE_REMINDER_OFFSETS)` — Zod 4's array form of `z.literal`,
 * which is a union of the eight literals rather than a schema for the array
 * itself.
 */
export const dueReminderOffsetSchema = z.literal(DUE_REMINDER_OFFSETS);

export type DueReminderOffsetMinutes = z.infer<typeof dueReminderOffsetSchema>;

/**
 * `PATCH /cards/:id/due-date-reminder` — `FR-CARD-014`. SC-4, field-scoped.
 *
 * ⚠ **409 when the card has no due date, for every value including `null`.**
 * `FR-CARD-014`'s whole subject is "when a due-date reminder fires", which
 * presupposes a due date to be relative to; a route that let this field be
 * set on a card with none would either create the orphan state
 * `cardDueDatePatchSchema` above exists to prevent, or (for `null`) accept a
 * request that changes nothing on a resource state that makes the request
 * meaningless. Refusing unconditionally is also the simpler contract for a
 * client: "you may touch the reminder once a due date exists" needs no
 * special case for "unless you are only clearing it".
 *
 * There is no `due.reminder_changed`: the offset change rides on
 * `due.date_changed` with `dueAt` unchanged in the payload — see
 * `packages/shared/src/events.ts`'s B24 entries.
 */
export const cardDueReminderPatchSchema = z.strictObject({
  dueReminderMinutesBefore: dueReminderOffsetSchema
    .nullable()
    .describe(
      'Minutes before the due date to send a reminder — one of ' +
        `${DUE_REMINDER_OFFSETS.join(', ')}, or null for “None” ` +
        '(FR-CARD-014). The card must already carry a due date; a 409 refuses ' +
        'this route on one that does not.',
    ),
});

export type CardDueReminderPatch = z.infer<typeof cardDueReminderPatchSchema>;

/**
 * `PATCH /cards/:id/completed` — `FR-CARD-004`. SC-4, field-scoped.
 *
 * ⚠ **Declared in stage 1 for a later stage to consume, and stage 2 is that
 * stage — corrected in place rather than deleted, because the way this
 * docstring was written is itself the pattern worth keeping.** Stage 1 said
 * "this schema has no route behind it yet"; `apps/server/src/routes/
 * card-dates.ts`'s `PATCH /cards/:id/completed` (`BE-24.4`) is now that
 * route, `cardDetailSchema` and `boardFaceCardSchema` both gained a
 * `completed` field with it (`./card.ts`'s rule: a field arrives with the
 * route that reads and writes it), and `due.date_completed` /
 * `due.date_uncompleted` are registered in `./events` for the identical
 * reason — the block that first emits a placeholder registers it then, and
 * stage 2 is "then".
 */
export const cardCompletionPatchSchema = z.strictObject({
  completed: z
    .boolean()
    .describe(
      'Whether the card is marked complete (FR-CARD-004). Independent of the ' +
        'due date — a card with no due date can be marked complete.',
    ),
});

export type CardCompletionPatch = z.infer<typeof cardCompletionPatchSchema>;
